import { styled, keyframes } from '@mui/material/styles';
import {
  Box,
  Button,
  Typography,
  TextField,
  Select,
  Switch,
  Radio,
  Autocomplete,
  Avatar,
  TextareaAutosize,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// ============================================
// Error Animation
// ============================================
const errorPulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(220, 38, 38, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);
  }
`;

export const ErrorFieldWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'hasError',
})(({ hasError }) => ({
  flex: 1,
  borderRadius: '8px',
  '& > *': {
    width: '100%',
  },
  ...(hasError && {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#dc2626 !important',
      borderWidth: '2px !important',
    },
    animation: `${errorPulseGlow} 1.5s ease-in-out infinite`,
  }),
}));

// ============================================
// Modal Container & Layout
// ============================================
export const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '520px',
  // 패스 카드가 여러 장이면 내용이 화면보다 길어질 수 있다 — 헤더/푸터는 고정하고 본문만 스크롤
  maxHeight: 'calc(100vh - 48px)',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#fff',
  boxShadow: '0px 11px 15px -7px rgba(0,0,0,0.2)',
  borderRadius: '16px',
  overflow: 'hidden',
});

export const ModalHeader = styled(Box)({
  height: '56px',
  backgroundColor: '#262626',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px',
});

export const ModalTitle = styled(Typography)({
  color: '#fff',
  fontSize: '18px',
  fontWeight: 600,
});

export const StyledCloseIcon = styled(CloseIcon)({
  color: '#fff',
  width: '24px',
  height: '24px',
  cursor: 'pointer',
});

export const ModalContent = styled(Box)({
  padding: '24px',
  overflowY: 'auto',
});

export const ModalContentNoPadTop = styled(Box)({
  padding: '0 24px',
});

// ============================================
// Form Row & Label
// ============================================
export const FormRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '16px 0',
  borderBottom: '1px solid #F0F0F0',
});

export const FormRowLast = styled(FormRow)({
  borderBottom: 'none',
});

export const FormLabel = styled(Typography)({
  width: '100px',
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
  flexShrink: 0,
});

export const FormInputRow = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const FormInputRowBetween = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

// ============================================
// Member Info (수정 모달)
// ============================================
export const MemberInfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '20px',
});

export const MemberInfoLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const MemberAvatar = styled(Avatar)({
  width: 48,
  height: 48,
  backgroundColor: '#E0E0E0',
});

export const MemberName = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#333',
});

export const MemberPhone = styled(Typography)({
  fontSize: '14px',
  color: '#666',
});

export const MemberDetailLink = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#1F3A5F',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
});

// ============================================
// Input Fields
// ============================================
export const MemoTextarea = styled(TextareaAutosize)({
  flex: 1,
  minHeight: '80px',
  padding: '10px 14px',
  borderRadius: '8px',
  border: '1px solid rgba(0, 0, 0, 0.23)',
  fontSize: '14px',
  fontFamily: 'inherit',
  resize: 'vertical',
  outline: 'none',
  '&:hover': {
    borderColor: 'rgba(0, 0, 0, 0.5)',
  },
  '&:focus': {
    borderColor: '#1F3A5F',
    borderWidth: '2px',
    padding: '9px 13px',
  },
  '&:disabled': {
    backgroundColor: '#f5f5f5',
    color: 'rgba(0, 0, 0, 0.38)',
    cursor: 'not-allowed',
  },
});

// 셀렉트(38px)와 줄 높이를 맞춘다 — 입력류는 기본 40~41px 로 미세하게 어긋난다.
export const StyledTextField = styled(TextField)({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    height: '38px',
    borderRadius: '8px',
  },
});

export const DisabledTextField = styled(TextField)({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    height: '38px',
    borderRadius: '8px',
    backgroundColor: '#fff',
    paddingRight: '8px',
  },
});

export const StyledSelect = styled(Select)({
  flex: 1,
  borderRadius: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E0E0E0',
  },
});

export const StyledSwitch = styled(Switch)({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#1F3A5F',
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#1F3A5F',
  },
});

export const StyledRadio = styled(Radio)({
  '&.Mui-checked': {
    color: '#1F3A5F',
  },
});

export const StyledAutocomplete = styled(Autocomplete)({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    height: '38px',
    padding: '0 9px',
    borderRadius: '8px',
  },
});

export const UnregisteredInputRow = styled(Box)({
  flex: 1,
  display: 'flex',
  gap: '12px',
});

export const DateSeparator = styled(Typography)({
  color: '#666',
});

// Typography(line-height 1.5)를 쓰면 플레이스홀더가 보이는 셀렉트만 높이가 부풀어
// (42px vs 값 표시 38px) 다른 필드와 어긋난다. span 은 셀렉트의 폰트·행간을 그대로 상속.
export const PlaceholderText = styled('span')({
  color: '#999',
});

// ============================================
// Buttons
// ============================================
export const LockerMoveButton = styled(Button)({
  height: '32px',
  minWidth: '72px',
  padding: '2px 10px',
  borderRadius: '6px',
  borderColor: '#E0E0E0',
  color: '#4A6A94',
  fontSize: '13px',
  fontWeight: 500,
  '&:hover': {
    borderColor: '#E0E0E0',
    backgroundColor: '#fff',
  },
});

// ============================================
// Footer
// ============================================
export const ModalFooter = styled(Box)({
  padding: '0 24px 24px 24px',
  display: 'flex',
  gap: '12px',
});

export const ModalFooterAssign = styled(Box)({
  padding: '16px 24px 24px 24px',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
});

export const EndUsageButton = styled(Button)({
  height: '48px',
  padding: '0 20px',
  borderRadius: '8px',
  border: '1px solid #EE735A',
  fontSize: '16px',
  fontWeight: 500,
  color: '#EE735A',
  '&:hover': {
    backgroundColor: 'rgba(238, 115, 90, 0.05)',
  },
});

export const CancelButton = styled(Button)({
  flex: 1,
  height: '48px',
  borderRadius: '8px',
  backgroundColor: '#D9D9D9',
  fontSize: '16px',
  fontWeight: 500,
  color: '#333',
  '&:hover': {
    backgroundColor: '#F5F5F5',
  },
});

export const SubmitButton = styled(Button)({
  flex: 1.5,
  height: '48px',
  borderRadius: '8px',
  backgroundColor: '#1F3A5F',
  fontSize: '16px',
  fontWeight: 500,
  color: '#fff',
  '&:hover': {
    backgroundColor: '#172C48',
  },
});
